import { supabase } from './supabaseClient';
import { BookingSplitRow, Report, ReportMetadata, ServiceItem, ServiceTransaction } from '../types';
import { loadProjectById, loadReportSnapshot, saveProjectToCloud } from './cloudService';
import { getServiceLog, mirrorEntriesToJournal } from './serviceJournal';
import { isHourlyUnit } from './projectLv';
import { BookingSkipNotice, applyReportBookingWithNotices, redirectMainBookingsToFlat } from './reportBooking';
import { UnbookedSentReportRow, isInspectionBilled } from './unbookedReportsLogic';
import { berlinZeitpunkt, buchungstag, isAccountingEntry } from './serviceLogUtils';
import { fetchProjectLock } from './projectLockService';
import { isLockHeldByOther, lockHolderName } from './projectLockLogic';
import { readOnlyMessage } from './readOnlyNotice';
import {
    RebookPreview,
    archivTag,
    archivUhrzeit,
    buildRebookPreview,
    childBookingDescription,
    flatRedirectHinweis,
    childBookingRef,
    istSofortBuchbar,
    proposeRebookTarget,
    rebookOpts,
} from './reportRebookLogic';
import { TAGESPAUSCHALE, billingModeAt } from './linkedBillingMode';
import { bookingIsFlatProof, isReportProofEntry, planFlatProofEntry } from './flatRateLogic';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * BEGEHUNG NACHBUCHEN — der schreibende Weg
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Zwei Vorgaenge, streng getrennt:
 *
 *   `ladeRebookKontext(row)`   liest alles zusammen, was der Dialog zeigt.
 *   `nachbuchen(...)`          schreibt genau EINE Buchung.
 *
 * Beide laden das Buchungsprojekt FRISCH vom Server. Der Client-Zustand einer
 * Aufgabenliste ist Minuten alt; in dieser Zeit kann der Bericht laengst
 * gebucht worden sein — und die zweite Buchung faellt erst auf der Rechnung
 * auf.
 *
 * Was hier bewusst NICHT passiert:
 *  · kein `updateProjectExportStatus`, kein `saveReportSnapshot` — beides
 *    gehoert zum VERSAND. Der Bericht ist laengst versendet und archiviert;
 *    ihn erneut zu archivieren ueberschriebe den Stand von damals.
 *  · kein Anfassen von `metadata.date` oder `metadata.intervalConfig`.
 *    `saveProjectToCloud` schreibt `last_inspection_date` und
 *    `next_scheduled_date` IMMER aus den Metadaten mit; werden sie nicht
 *    veraendert, bleibt der Terminplan stehen. Genau das ging bei Vorfall 6146
 *    schief, als ein rueckdatierter Vorgang die Termine neu schrieb.
 *  · kein Massenlauf. Ein Fall, ein Aufruf.
 */

/**
 * Kennung einer Protokollzeile fuer den Vorher/Nachher-Vergleich: die id, und
 * wo sie fehlt der Inhalt.
 */
const kennung = (e: ServiceTransaction | null | undefined): string =>
    String((e as any)?.id ?? '').trim()
    || [e?.serviceId, e?.date, e?.quantity, e?.description, (e as any)?.reportRef]
        .map(v => String(v ?? ''))
        .join('|');

/**
 * Die in diesem Vorgang NEU entstandenen Protokollzeilen.
 *
 * Verglichen und nicht geschnitten: `applyReportBookingWithNotices` haengt
 * zwar hinten an, aber wie viele Zeilen entstehen, entscheiden Riegel und
 * Aufteilung erst zur Laufzeit. Sich an eine Arrayposition zu haengen ist
 * genau der Fehler, den die Journal-Schluesselvergabe bewusst vermeidet.
 */
const neueEintraege = (
    vorher: ServiceTransaction[],
    nachher: ServiceTransaction[],
): ServiceTransaction[] => {
    const bekannt = new Set((vorher || []).map(kennung));
    return (nachher || []).filter(e => !bekannt.has(kennung(e)));
};

export interface RebookKontext {
    row: UnbookedSentReportRow;
    /** Das Projekt, in dessen Protokoll gebucht wird (bei einer Tochter: die Mutter). */
    buchungsProjektId: string;
    buchungsProjektName: string | null;
    /** Der frische Report des Buchungsprojekts — Grundlage jeder Pruefung. */
    buchungsReport: Report;
    services: ServiceItem[];
    serviceLog: ServiceTransaction[];
    /** Der archivierte Bericht, falls vorhanden. */
    snapshot: Report | null;
    inspectionTime: string | null;
    /** Kein Archiveintrag — dann gibt es keinen Begehungstag und nichts zu buchen. */
    archivFehlt: boolean;
    /** Tochter mit Tagespauschale — aendert Wortlaut UND Riegel. */
    tagespauschale: boolean;
    /** Ein anderer haelt die Projektsperre; sein Name steht daneben. */
    fremdGesperrt: boolean;
    sperrHalter: string | null;
    /** Name des Durchfuehrenden aus dem Archiv. */
    performerId: string;
    performerName: string;
    /** Vorschlag samt Herkunft. */
    vorschlag: ReturnType<typeof proposeRebookTarget>;
    /** Der Name der Tochter — nur fuer die Beschreibung der Mutterbuchung. */
    tochterName: string | null;
    preview: RebookPreview;
}

export interface RebookErgebnis {
    ok: boolean;
    message: string;
    /** Jede uebersprungene Buchung — muss angezeigt werden, nie verschlucken. */
    skipped: BookingSkipNotice[];
}

/** Uhrzeit und Snapshot des Archiveintrags. */
const ladeArchiv = async (reportHistoryId: string): Promise<{ snapshot: Report | null; inspectionTime: string | null; fehlt: boolean }> => {
    const { data } = await supabase
        .from('report_history')
        .select('id, inspection_time')
        .eq('id', reportHistoryId)
        .maybeSingle();
    if (!data) return { snapshot: null, inspectionTime: null, fehlt: true };
    // Lesemodus: das Archiv versendeter Dokumente bleibt unberuehrt.
    const snapshot = await loadReportSnapshot(reportHistoryId, undefined, { skipMigration: true });
    return { snapshot, inspectionTime: (data as any).inspection_time || null, fehlt: false };
};

/**
 * Alles laden, was der Dialog braucht — und nichts entscheiden.
 *
 * `abrechnung` kommt vom Aufrufer (der Befund kennt sie aus
 * `details.abrechnung`); dieses Modul erfindet keine zweite Quelle dafuer.
 */
export const ladeRebookKontext = async (
    row: UnbookedSentReportRow,
    optionen?: {
        istSchreibgeschuetzt?: boolean;
        sperrHalter?: string | null;
        abrechnung?: { stand?: string; beleg?: string; von?: string; bis?: string } | null;
    },
): Promise<RebookKontext> => {
    const buchungsProjektId = row.parent_project_id || row.project_id;
    const [projekt, archiv] = await Promise.all([
        // `skipMigration`: das blosse Oeffnen des Dialogs ist ein LESEVORGANG.
        // Ohne den Schalter zieht `loadProjectById` Selbstheilungs-Migrationen
        // nach und schreibt in `inspection_items` — an einem Projekt, das der
        // Bearbeiter womoeglich gar nicht anfasst.
        loadProjectById(buchungsProjektId, undefined, { skipMigration: true }),
        ladeArchiv(row.report_history_id),
    ]);
    if (!projekt?.data) throw new Error('Das Buchungsprojekt konnte nicht geladen werden.');

    const buchungsReport = projekt.data as Report;
    const meta = buchungsReport.metadata as ReportMetadata;
    const services = (meta.globalData?.services || []) as ServiceItem[];
    const serviceLog: ServiceTransaction[] = getServiceLog(buchungsReport);

    // Bei einer Tochter steht die Zielposition im LV DER MUTTER, der Vorschlag
    // aus dem Archiv nennt aber die Position, die im Kind gewaehlt war — beim
    // Kind-zu-Mutter-Pfad ist das dieselbe id, weil die Tochter auf das LV der
    // Mutter bucht.
    const vorschlag = proposeRebookTarget(archiv.snapshot, services, serviceLog);

    // Die Abrechnungsart der Tochter entscheidet ueber den Wortlaut der
    // Warnung UND darueber, ob derselbe Tag spaeter sperrt. Sie steht am KIND —
    // und zwar in der Fassung vom BEGEHUNGSTAG. Nachbuchen ist per Definition
    // rueckwirkend: die heutige Art zu nehmen beurteilte eine Begehung vom Mai
    // nach einer Umstellung vom Juli und erzeugte damit genau die Warnung, die
    // `billingModeHistory` verhindern soll.
    let tagespauschale = false;
    if (row.parent_project_id) {
        const kind = await loadProjectById(row.project_id, undefined, { skipMigration: true });
        const kindConfig = (kind?.data as Report | undefined)?.metadata?.globalData?.linkedProjectConfig;
        // `archivTag` und nicht das Rohfeld: der schreibende Pfad normalisiert
        // ebenso (`const tag = archivTag(row.inspection_date)`). Ein voller
        // Zeitstempel — lokale Mitternacht des 05.05. ist in MEZ
        // `2026-05-04T22:00Z` — ergaebe hier per Textschnitt den 04.05. und
        // dort den 05.05. Liegt dazwischen eine Umstellung, widerspraeche die
        // Vorschau der Buchung, die sie ankuendigt.
        tagespauschale = billingModeAt(kindConfig, archivTag(row.inspection_date)) === TAGESPAUSCHALE;
    }

    // Sperre und Schreibschutz gehoeren in die VORSCHAU, nicht erst in die
    // Absage nach dem Klick: wer sieht, dass ein Kollege drinsitzt, klickt gar
    // nicht erst.
    const sperre = await fetchProjectLock(buchungsProjektId);
    const eigeneId = (await supabase.auth.getUser()).data.user?.id ?? null;
    const fremdGesperrt = isLockHeldByOther(sperre, eigeneId, Date.now());

    // Der Durchfuehrende ist der Mann von DAMALS, nicht der Bearbeiter von
    // heute: `performerName` steht spaeter im Leistungsnachweis neben der
    // Begehung. Das Archiv fuehrt nur seinen Namen (`conductedBy`), keine
    // Kennung — deshalb 'system' als Kennung, wie im Kind-zu-Mutter-Pfad.
    // Dass hier nachgebucht wurde, verraet der Abstand zwischen `date`
    // (Begehungstag) und `bookedAt` (heute), nicht der Name.
    const snapMeta = archiv.snapshot?.metadata as ReportMetadata | undefined;
    const performerName = row.conductor_name || snapMeta?.conductedBy || 'Unbekannt';
    const performerId = 'system';

    const preview = buildRebookPreview({
        row,
        services,
        serviceLog,
        bookings: vorschlag.bookings,
        snapshot: archiv.snapshot,
        inspectionTime: archiv.inspectionTime,
        istSchreibgeschuetzt: optionen?.istSchreibgeschuetzt || fremdGesperrt,
        sperrHalter: optionen?.sperrHalter ?? (fremdGesperrt ? lockHolderName(sperre) : null),
        abrechnung: optionen?.abrechnung,
        archivFehlt: archiv.fehlt,
        tagespauschale,
    });

    return {
        row,
        buchungsProjektId,
        buchungsProjektName: (projekt as any).name ?? row.project_name,
        buchungsReport,
        services,
        serviceLog,
        snapshot: archiv.snapshot,
        inspectionTime: archiv.inspectionTime,
        archivFehlt: archiv.fehlt,
        tagespauschale,
        fremdGesperrt,
        sperrHalter: fremdGesperrt ? lockHolderName(sperre) : null,
        performerId,
        performerName,
        vorschlag,
        tochterName: row.parent_project_id ? row.project_name : null,
        preview,
    };
};

/**
 * Die Vorschau mit einer anderen Zielposition neu rechnen — ohne erneut zu
 * laden. Der Bearbeiter darf den Vorschlag aendern; die Mengenregel bleibt
 * dieselbe, weil `buildRebookPreview` `resolveMainBookings` benutzt.
 */
export const rechneVorschauNeu = (
    kontext: RebookKontext,
    bookings: BookingSplitRow[],
    optionen?: {
        istSchreibgeschuetzt?: boolean;
        sperrHalter?: string | null;
        abrechnung?: { stand?: string; beleg?: string; von?: string; bis?: string } | null;
    },
): RebookPreview => buildRebookPreview({
    row: kontext.row,
    services: kontext.services,
    serviceLog: kontext.serviceLog,
    bookings,
    snapshot: kontext.snapshot,
    inspectionTime: kontext.inspectionTime,
    istSchreibgeschuetzt: optionen?.istSchreibgeschuetzt || kontext.fremdGesperrt,
    sperrHalter: optionen?.sperrHalter ?? kontext.sperrHalter,
    abrechnung: optionen?.abrechnung,
    archivFehlt: kontext.archivFehlt,
    tagespauschale: kontext.tagespauschale,
});

/**
 * NACHBUCHEN — der einzige schreibende Aufruf.
 *
 * Wirft nicht; jeder Ausgang kommt als Text zurueck und gehoert in den Dialog.
 */
export const nachbuchen = async (args: {
    row: UnbookedSentReportRow;
    bookings: BookingSplitRow[];
    /** Muss aus `ladeRebookKontext` stammen — nicht aus dem Listenzustand. */
    performerId: string;
    performerName: string;
    snapshot?: Report | null;
    inspectionTime?: string | null;
    tochterName?: string | null;
}): Promise<RebookErgebnis> => {
    const { row } = args;
    const tag = archivTag(row.inspection_date);
    if (!tag) {
        return { ok: false, message: 'Ohne Begehungstag aus dem Archiv wird nicht gebucht.', skipped: [] };
    }
    if (args.bookings.length === 0) {
        return { ok: false, message: 'Es ist keine Zielposition gewählt — es wurde nichts gebucht.', skipped: [] };
    }

    const buchungsProjektId = row.parent_project_id || row.project_id;

    // ── Sperre, unmittelbar vor dem Schreiben ──────────────────────────────
    //
    // Dieses Werkzeug schreibt an der Oberflaeche vorbei einen VOLLSTAENDIGEN
    // Projektstand zurueck (`saveProjectToCloud`, last write wins). Sitzt ein
    // Kollege im Projekt, verliert einer von beiden seine Arbeit — er seine
    // Maengel oder wir die Buchung. Die Anwendung hat dafuer die Projektsperre;
    // sie zu uebergehen, weil dieser Weg aus der Verwaltung kommt, waere genau
    // die Abkuerzung, die spaeter niemand mehr erklaeren kann.
    //
    // Abgebrochen wird MIT Text, nie still — derselbe Wortlaut wie ueberall.
    const sperre = await fetchProjectLock(buchungsProjektId);
    const eigeneId = (await supabase.auth.getUser()).data.user?.id ?? null;
    if (isLockHeldByOther(sperre, eigeneId, Date.now())) {
        return {
            ok: false,
            message: readOnlyMessage(
                `Bericht #${row.inspection_number} wurde NICHT gebucht.`,
                lockHolderName(sperre),
            ),
            skipped: [],
        };
    }

    // ── Letzter Stand vom Server, unmittelbar vor dem Schreiben ────────────
    const projekt = await loadProjectById(buchungsProjektId);
    if (!projekt?.data) {
        return { ok: false, message: 'Das Buchungsprojekt konnte nicht geladen werden — es wurde nichts gebucht.', skipped: [] };
    }
    const report = projekt.data as Report;
    const meta = report.metadata as ReportMetadata;
    const services = (meta.globalData?.services || []) as ServiceItem[];
    const serviceLog: ServiceTransaction[] = getServiceLog(report);

    if (row.parent_project_id) {
        // Die Abrechnungsart steht am KIND (`linkedProjectConfig`), nicht an der
        // Mutter — und sie entscheidet, ob zwei Toechter am selben Tag EINE
        // Leistung sind (Tagespauschale) oder zwei. Ohne diese Angabe waere der
        // Tagesriegel unten falsch: bei Einzelabrechnung ist derselbe Tag der
        // NORMALFALL, weil ein Koordinator die Abschnitte nacheinander begeht.
        const kind = await loadProjectById(row.project_id);
        const config = (kind?.data as Report | undefined)?.metadata?.globalData?.linkedProjectConfig;
        return nachbuchenTochter({
            ...args,
            tag,
            report,
            services,
            serviceLog,
            buchungsProjektId,
            tagespauschale: billingModeAt(config, tag) === TAGESPAUSCHALE,
        });
    }

    // Nur das Praedikat der Sicht. `isReportBooked` gehoert hier nicht her: es
    // schlaegt schon bei einer gebuchten ZUSATZLEISTUNG an (die denselben
    // reportRef traegt) und sperrte damit ausgerechnet den Fall
    // `nur_zusatzleistung_gebucht` — die Begehung fehlt dort wirklich.
    // Ausfuehrliche Begruendung an derselben Pruefung in reportRebookLogic.ts.
    const schonGebucht = isInspectionBilled(
        serviceLog as any, row.inspection_number, row.section_id, row.inspection_date, row.parent_project_id,
    );
    if (schonGebucht) {
        return {
            ok: false,
            message: `Bericht #${row.inspection_number} ist inzwischen bereits gebucht — es wurde nichts geschrieben.`,
            skipped: [],
        };
    }

    const opts = rebookOpts({
        row,
        bookings: args.bookings,
        snapshot: args.snapshot,
        inspectionTime: args.inspectionTime,
        performerId: args.performerId,
        performerName: args.performerName,
    });

    // Derselbe Pfad wie beim Versand — samt Ref-Dedup, Tagesriegel und
    // `no_booking_target`. Danebenzuschreiben hiesse, drei Riegel zu umgehen.
    const { report: roh, skipped, redirectedToFlat } = applyReportBookingWithNotices(report, opts);

    // ── `isExported` ZURUECKSTELLEN — der gefaehrlichste Nebeneffekt ───────
    //
    // `applyReportBookingWithNotices` setzt `metadata.isExported = true`, weil
    // es fuer den VERSAND gebaut ist: dort ist der bearbeitete Bericht genau
    // der, der gerade rausgeht.
    //
    // Hier ist er das nicht. Nachgebucht wird ein Bericht aus dem ARCHIV; der
    // frisch geladene Report traegt aber die LAUFENDE Begehung des Projekts —
    // oft eine spaetere, noch gar nicht versendete. Bliebe das Flag stehen,
    // waere diese laufende Begehung faelschlich als exportiert markiert, und
    // beim naechsten Versand schaltet `billingEnabled` (EmailModal) die
    // Abrechnung ab: der Bericht ginge raus und wuerde NIE gebucht.
    //
    // Das Werkzeug erzeugte damit genau die Luecke, die es schliessen soll —
    // nur eine Begehung weiter. Deshalb bleibt der Wert, wie er war.
    const gebucht: Report = {
        ...roh,
        metadata: { ...roh.metadata, isExported: report.metadata.isExported },
    } as Report;

    const wurdeGebucht = getServiceLog(gebucht).length > serviceLog.length;
    if (!wurdeGebucht) {
        return {
            ok: false,
            message: skipped[0]?.message || 'Es wurde nichts gebucht.',
            skipped,
        };
    }

    try {
        await saveProjectToCloud(gebucht, buchungsProjektId);
    } catch (e: any) {
        return { ok: false, message: `Speichern fehlgeschlagen: ${e?.message || 'Unbekannter Fehler'}`, skipped };
    }

    // ── Zweitschrift ins Journal — nur die HINZUGEKOMMENEN Zeilen ──────────
    //
    // Erst nach dem BESTAETIGTEN Speichern und ausserhalb des try darueber:
    // das Anhaengen darf den Ausgang des Nachbuchens nicht mehr aendern.
    //
    // `applyReportBookingWithNotices` haengt Haupt- und Zusatzbuchungen ans
    // ENDE des Protokolls, kann aber auch nichts schreiben (Riegel). Deshalb
    // wird verglichen statt gezaehlt oder geschnitten. Keine `statements`: die
    // Stelle bucht Haupt- und Zusatzleistung im selben Aufruf und weiss je
    // Zeile nichts, was der Text nicht auch hergibt.
    await mirrorEntriesToJournal({
        projectId: buchungsProjektId,
        entries: neueEintraege(serviceLog, getServiceLog(gebucht)),
        previousLog: serviceLog,
        source: 'rebook',
        linkedProjectConfig: meta.globalData?.linkedProjectConfig ?? null,
    });

    // Das Netz in book() hat eine gedeckte Begehungsposition auf die
    // Pauschale gelenkt — das gehoert in die Meldung, nicht nur ins Protokoll.
    const umgelenkt = flatRedirectHinweis(redirectedToFlat, services);
    return {
        ok: true,
        message: `Bericht #${row.inspection_number} vom ${tag.split('-').reverse().join('.')} wurde nachgebucht.`
            + (umgelenkt ? ` ${umgelenkt}` : ''),
        skipped,
    };
};

/**
 * Der Kind-zu-Mutter-Pfad.
 *
 * `applyReportBookingWithNotices` ist hier NICHT der richtige Weg: es schriebe
 * `RPT-<section>-<nr>` ins Protokoll der Mutter, und die Sicht sucht dort
 * `RPT-CHILD-<kindId>-<nr>`. Der Fall bliebe stehen. Deshalb dieselbe Form wie
 * `SiGeKoInspectionModule` sie beim Versand schreibt — mit demselben
 * Idempotenz-Riegel ueber den Ref, und ohne Aufteilung auf mehrere Lose.
 */
const nachbuchenTochter = async (args: {
    row: UnbookedSentReportRow;
    bookings: BookingSplitRow[];
    performerId: string;
    performerName: string;
    tochterName?: string | null;
    tag: string;
    report: Report;
    services: ServiceItem[];
    serviceLog: ServiceTransaction[];
    buchungsProjektId: string;
    /** `linkedProjectConfig.billingMode === 'daily_flat'` am KIND. */
    tagespauschale: boolean;
    inspectionTime?: string | null;
    snapshot?: Report | null;
}): Promise<RebookErgebnis> => {
    const { row, tag, report, services, serviceLog, buchungsProjektId } = args;

    if (args.bookings.length > 1) {
        return {
            ok: false,
            message: 'Bei einem Tochterprojekt lässt sich eine Begehung nicht auf mehrere Lose aufteilen — dieselbe Sperre gilt beim Versand.',
            skipped: [],
        };
    }

    const ref = childBookingRef(row.project_id, row.inspection_number);
    if (serviceLog.some(l => (l as any)?.reportRef === ref)) {
        return {
            ok: false,
            message: `Bericht #${row.inspection_number} ist im Protokoll des Mutterprojekts bereits gebucht — es wurde nichts geschrieben.`,
            skipped: [],
        };
    }

    // Dasselbe Netz wie der eigene Pfad (applyReportBookingWithNotices): eine
    // von der Begehungspauschale gedeckte Begehungsposition geht auf die
    // Pauschale — im Pauschalprojekt bekommt 2.7.10 nichts mehr, auch nicht
    // ueber eine Tochter.
    const { rows: ziele, redirectedToFlat } = redirectMainBookingsToFlat(args.bookings, services);
    const index = services.findIndex(s => s.id === ziele[0]?.serviceId);
    if (index === -1) {
        return {
            ok: false,
            message: 'Die gewählte Position gibt es im Leistungsverzeichnis des Mutterprojekts nicht — es wurde nichts gebucht.',
            skipped: [],
        };
    }
    const ziel = services[index];

    // ── Pauschale der Mutter: NACHWEISZEILE statt Menge ───────────────────
    //
    // Frueher hob dieser Pfad quantityUsed der gewaehlten Position um 1 (bzw.
    // die Stunden) an — auf einer Pauschale ein Monat Geld ohne
    // FLAT-Schluessel. Jetzt: Menge 0, NW-Referenz, der Kind-Ref bleibt (die
    // Sicht erkennt den Bericht daran), quantityUsed/status unberuehrt. Der
    // Tagesriegel unten entfaellt: eine Nachweiszeile kostet nichts, und der
    // Ref-Riegel oben haelt den Bericht trotzdem einmalig.
    if (bookingIsFlatProof(ziel)) {
        return nachweisAufPauschale({ ...args, ref, pauschale: ziel, umgelenkt: flatRedirectHinweis(redirectedToFlat, services) });
    }

    // ── Tagesriegel: NUR bei Tagespauschale ───────────────────────────────
    //
    // Bei einer Tagespauschale ist der Tag die abrechenbare Einheit: begeht ein
    // Koordinator drei Lose an einem Tag, ist das EINE Leistung — genau das
    // prueft der Versandpfad gruppenweit ueber alle Begehungspositionen.
    //
    // Bei Einzelabrechnung waere derselbe Riegel schlicht falsch. Mutter und
    // Toechter am selben Tag zu begehen ist dort der Normalfall und nicht die
    // Ausnahme; jede Tochter hat ihre eigene Leistung. Ein Riegel ueber alle
    // Begehungspositionen der Mutter verschluckte reihenweise erbrachte
    // Begehungen — und zwar lautlos, weil die Aufgabe danach als „gebucht"
    // gaelte. Der Ref-Riegel oben bleibt in BEIDEN Faellen scharf; er ist der,
    // der eine echte Doppelbuchung desselben Berichts verhindert.
    const inspektionsIds = new Set(services.filter(s => s.assignment === 'inspection').map(s => s.id));
    // Der Tag in Europe/Berlin (`buchungstag`, S5d) — wie der Versandpfad und
    // die Journal-Spalte `entry_day`; bis S5d Geraetezeit (`archivTag`).
    const schonAnDiesemTag = args.tagespauschale && serviceLog.some(l =>
        buchungstag((l as any)?.date) === tag
        && inspektionsIds.has((l as any)?.serviceId)
        && ((l as any)?.quantity || 0) > 0
        // Eine BU-/VZ-/RN-/MB-Zeile ist ein BUCHHALTUNGSvorgang und keine
        // erbrachte Leistung. Zaehlte sie mit, meldete der Riegel „der Tag ist
        // abgerechnet", weil an diesem Tag eine RECHNUNG geschrieben wurde —
        // der Versandpfad filtert sie aus demselben Grund heraus.
        && !isAccountingEntry(l as any)
        && String((l as any)?.reportRef || '') !== ref,
    );
    if (schonAnDiesemTag) {
        return {
            ok: false,
            message:
                `Dieses Projekt rechnet als TAGESPAUSCHALE ab, und am ${tag.split('-').reverse().join('.')} ist im `
                + 'Mutterprojekt bereits eine Begehung gebucht. Der Tag ist damit abgerechnet — die Buchung wurde '
                + 'übersprungen, damit derselbe Tag nicht zweimal in Rechnung geht.',
            skipped: [{ reason: 'same_day_same_section', message: 'Tagespauschale: der Tag ist im Mutterprojekt bereits gebucht.' }],
        };
    }

    const menge = isHourlyUnit(ziel.unit) ? args.bookings[0].quantity : 1;
    const neuVerbraucht = (ziel.quantityUsed || 0) + menge;
    const fertig = ziel.quantityContracted > 0 && neuVerbraucht >= ziel.quantityContracted;
    const neueServices = [...services];
    neueServices[index] = { ...ziel, quantityUsed: neuVerbraucht, status: fertig ? 'done' : ziel.status };

    const eintrag: ServiceTransaction = {
        id: crypto.randomUUID(),
        // In Europe/Berlin wie `book()` (S5d); bis dahin Geraetezeit.
        date: berlinZeitpunkt(tag, archivUhrzeit(args.snapshot, args.inspectionTime))
            ?? new Date(`${tag}T${archivUhrzeit(args.snapshot, args.inspectionTime)}:00`).toISOString(),
        bookedAt: new Date().toISOString(),
        serviceId: ziel.id,
        quantity: menge,
        performerId: args.performerId,
        performerName: args.performerName,
        isSubstitution: false,
        description: childBookingDescription(
            args.tochterName || row.project_name || 'Tochter',
            row.inspection_number,
            menge,
            ziel.unit,
        ),
        reportRef: ref,
    } as ServiceTransaction;

    const aktualisiert: Report = {
        ...report,
        metadata: {
            ...report.metadata,
            globalData: {
                ...report.metadata.globalData,
                services: neueServices,
                serviceLog: [eintrag, ...serviceLog],
            },
        },
    } as Report;

    try {
        await saveProjectToCloud(aktualisiert, buchungsProjektId);
    } catch (e: any) {
        return { ok: false, message: `Speichern fehlgeschlagen: ${e?.message || 'Unbekannter Fehler'}`, skipped: [] };
    }

    // Zweitschrift ins Journal, erst nach dem BESTAETIGTEN Speichern.
    // Geschrieben wurde ins Protokoll der MUTTER, also gehen deren Kennung und
    // deren Protokollstand mit — nicht die der Tochter. Die Aussage ist hier
    // sicher: genau eine Begehung, keine Zusatzleistung.
    await mirrorEntriesToJournal({
        projectId: buchungsProjektId,
        entries: [eintrag],
        previousLog: serviceLog,
        source: 'rebook',
        linkedProjectConfig: report.metadata.globalData?.linkedProjectConfig ?? null,
        statements: { [eintrag.id]: { isAddon: false, coversInspection: true } },
    });

    return {
        ok: true,
        message: `Bericht #${row.inspection_number} wurde im Protokoll des Mutterprojekts nachgebucht (${menge} ${ziel.unit}).`,
        skipped: [],
    };
};

/**
 * Die Nachweiszeile einer Tochter auf der Pauschale der Mutter.
 *
 * Beschreibung in der Form der Kind-Buchung (KIND_HAUPTBUCHUNG erkennt den
 * Anfang), die Klammer sagt „1 Nachweis" statt einer Menge — dieselbe Form
 * schreibt der Versand (SiGeKoInspectionModule).
 */
const nachweisAufPauschale = async (args: {
    row: UnbookedSentReportRow;
    performerId: string;
    performerName: string;
    tochterName?: string | null;
    tag: string;
    report: Report;
    services: ServiceItem[];
    serviceLog: ServiceTransaction[];
    buchungsProjektId: string;
    inspectionTime?: string | null;
    snapshot?: Report | null;
    ref: string;
    pauschale: ServiceItem;
    /** Gewaehlt war eine gedeckte Begehungsposition — das sagt die Meldung. */
    umgelenkt?: string | null;
}): Promise<RebookErgebnis> => {
    const { row, tag, report, services, serviceLog, buchungsProjektId, ref, pauschale } = args;

    const eintrag = planFlatProofEntry({
        id: crypto.randomUUID(),
        flatServiceId: pauschale.id,
        // In Europe/Berlin wie `book()` (S5d) — derselbe Zeitpunkt wie die Mengenbuchung.
        date: berlinZeitpunkt(tag, archivUhrzeit(args.snapshot, args.inspectionTime))
            ?? new Date(`${tag}T${archivUhrzeit(args.snapshot, args.inspectionTime)}:00`).toISOString(),
        bookedAt: new Date().toISOString(),
        performerId: args.performerId,
        performerName: args.performerName,
        description: childBookingDescription(
            args.tochterName || row.project_name || 'Tochter',
            row.inspection_number,
            1,
            'Nachweis',
        ),
        reportRef: ref,
    });

    const aktualisiert: Report = {
        ...report,
        metadata: {
            ...report.metadata,
            globalData: {
                ...report.metadata.globalData,
                // Unveraendert: die Pauschale zaehlt ihre Monate nur ueber FLAT-JJJJ-MM.
                services,
                serviceLog: [eintrag, ...serviceLog],
            },
        },
    } as Report;

    try {
        await saveProjectToCloud(aktualisiert, buchungsProjektId);
    } catch (e) {
        // Supabase wirft auch schlichte Objekte mit `message` — daher kein instanceof Error.
        const meldung = (e as { message?: string } | null | undefined)?.message;
        return { ok: false, message: `Speichern fehlgeschlagen: ${meldung || 'Unbekannter Fehler'}`, skipped: [] };
    }

    await mirrorEntriesToJournal({
        projectId: buchungsProjektId,
        entries: [eintrag],
        previousLog: serviceLog,
        source: 'rebook',
        linkedProjectConfig: report.metadata.globalData?.linkedProjectConfig ?? null,
        // Mit Berichtsbezug (RPT-CHILD-…) belegt die Nachweiszeile die Begehung.
        statements: { [eintrag.id]: { isAddon: false, coversInspection: isReportProofEntry(eintrag) } },
    });

    return {
        ok: true,
        message: args.umgelenkt
            ? `Bericht #${row.inspection_number} wurde im Protokoll des Mutterprojekts nachgebucht. ${args.umgelenkt}`
            : `Bericht #${row.inspection_number} wurde im Protokoll des Mutterprojekts als Nachweis auf der Monatspauschale nachgebucht – der Monat ändert sich nicht.`,
        skipped: [],
    };
};

/** Ausgang des Schnellwegs: gebucht, oder das Fenster muss her. */
export type SofortErgebnis =
    | { status: 'gebucht'; ergebnis: RebookErgebnis }
    /** Der Vorgang ist gelaufen und gescheitert — Text zeigen, kein Fenster. */
    | { status: 'fehlgeschlagen'; ergebnis: RebookErgebnis }
    /** Riegel oder Warnungen: der Bearbeiter muss lesen, bevor er bucht. */
    | { status: 'dialog'; preview: RebookPreview };

/**
 * SCHNELLWEG — ein Klick, wenn es nichts zu entscheiden gibt.
 *
 * Laedt denselben frischen Serverstand wie der Dialog, rechnet dieselbe
 * Vorschau mit dem VORSCHLAG als Ziel und bucht ueber denselben schreibenden
 * Pfad (`nachbuchen`). Es gibt keine zweite Mengenregel und keinen zweiten
 * Riegel — der Schnellweg unterscheidet sich vom Dialog nur darin, dass er
 * bei einer leeren Vorschau (kein Riegel, keine Warnung) nicht fragt.
 *
 * Sobald die Vorschau etwas zu sagen hat, gibt er `dialog` zurueck und der
 * Aufrufer oeffnet das Fenster mit genau diesen Hinweisen. So bleibt der Satz
 * aus dem Dialog wahr: wer nur einen Knopf „Buchen" anbietet, bekommt
 * Buchungen — hier bekommt er sie nur dort, wo der Dialog ohnehin nichts
 * anzumerken haette.
 */
export const sofortNachbuchen = async (
    row: UnbookedSentReportRow,
    abrechnung?: { stand?: string; beleg?: string; von?: string; bis?: string } | null,
): Promise<SofortErgebnis> => {
    const kontext = await ladeRebookKontext(row, { abrechnung });
    if (!istSofortBuchbar(kontext.preview)) return { status: 'dialog', preview: kontext.preview };

    const ergebnis = await nachbuchen({
        row,
        bookings: kontext.vorschlag.bookings,
        performerId: kontext.performerId,
        performerName: kontext.performerName,
        snapshot: kontext.snapshot,
        inspectionTime: kontext.inspectionTime,
        tochterName: kontext.tochterName,
    });
    return ergebnis.ok ? { status: 'gebucht', ergebnis } : { status: 'fehlgeschlagen', ergebnis };
};
